import React, { useState, useRef, useEffect } from 'react';
import * as tf from '@tensorflow/tfjs';
import * as cocoSsd from '@tensorflow-models/coco-ssd';
import {
  ChakraProvider,
  Box,
  VStack,
  Heading,
  Button,
  Text,
  Container,
  Spinner,
  useToast,
  Image,
  Progress,
} from '@chakra-ui/react';

interface Light {
  id: string;
  x: number;
  y: number;
  productId: string;
}

interface Room {
  id: string;
  name: string;
  area: number;
  lights: Light[];
  bbox: [number, number, number, number];
}

interface LightProduct {
  id: string;
  name: string;
  lumens: number;
}

function App() {
  const [image, setImage] = useState<string | null>(null);
  const [imageUploaded, setImageUploaded] = useState(false);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [lightProducts, setLightProducts] = useState<LightProduct[]>([]);
  const [hoveredLight, setHoveredLight] = useState<Light | null>(null);
  const [loading, setLoading] = useState(false);
  const [modelLoaded, setModelLoaded] = useState(false);
  const [modelLoading, setModelLoading] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const modelRef = useRef<cocoSsd.ObjectDetection | null>(null);
  const toast = useToast();

  useEffect(() => {
    fetchLightProducts();
    loadModel();
  }, []);

  const loadModel = async () => {
    setModelLoading(true);
    try {
      const model = await cocoSsd.load();
      modelRef.current = model;
      setModelLoaded(true);
      toast({
        title: 'Model loaded',
        description: 'The object detection model has been successfully loaded.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Failed to load COCO-SSD model:', error);
      toast({
        title: 'Error',
        description: 'Failed to load the object detection model. Please refresh the page and try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
    setModelLoading(false);
  };

  const fetchLightProducts = async () => {
    setLoading(true);
    try {
      // NOTE: This is mock data. In a production environment, you would integrate with the Philips Hue API.
      // To properly integrate with the Philips Hue API:
      // 1. Set up a Philips Hue developer account and create an app
      // 2. Implement the OAuth 2.0 flow to get an access token
      // 3. Use the access token to make authenticated requests to the Philips Hue API
      // 4. Handle rate limiting and other API-specific requirements
      // For more information, visit: https://developers.meethue.com/
      const mockProducts: LightProduct[] = [
        { id: '1', name: 'Hue White', lumens: 800 },
        { id: '2', name: 'Hue Color', lumens: 1100 },
        { id: '3', name: 'Hue Filament', lumens: 550 },
        { id: '4', name: 'Hue Bloom', lumens: 500 },
        { id: '5', name: 'Hue Go', lumens: 520 },
      ];
      setLightProducts(mockProducts);
    } catch (error) {
      console.error('Failed to fetch light products:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch light products. Using default data.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
    setLoading(false);
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImage(reader.result as string);
        setImageUploaded(true);
        toast({
          title: 'Image uploaded',
          description: 'Your layout image has been successfully uploaded.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      };
      reader.onerror = () => {
        toast({
          title: 'Error',
          description: 'Failed to upload the image. Please try again.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const analyzeLayout = async () => {
    if (!image || !imageRef.current || !modelRef.current) return;

    setLoading(true);
    try {
      // Perform object detection
      const predictions = await modelRef.current.detect(imageRef.current);

      // Filter and process room predictions
      const roomPredictions = predictions.filter((pred) => 
        ['room', 'bedroom', 'living room', 'kitchen', 'bathroom'].includes(pred.class.toLowerCase())
      );

      const detectedRooms = roomPredictions.map((pred, index) => ({
        id: `r${index + 1}`,
        name: pred.class,
        area: calculateArea(pred.bbox),
        lights: [],
        bbox: pred.bbox as [number, number, number, number],
      }));

      setRooms(detectedRooms);
      toast({
        title: 'Layout analyzed',
        description: `Detected ${detectedRooms.length} rooms`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error analyzing layout:', error);
      toast({
        title: 'Error',
        description: 'Failed to analyze layout',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
    setLoading(false);
  };

  const calculateArea = (bbox: [number, number, number, number]): number => {
    const [, , width, height] = bbox;
    // Assuming 1 pixel = 0.1 square meters (adjust as needed)
    return (width * height * 0.01);
  };

  const calculateLighting = () => {
    const updatedRooms = rooms.map(room => {
      const requiredLumens = room.area * 250; // 250 lumens per square meter
      const lights: Light[] = [];
      let currentLumens = 0;
      
      while (currentLumens < requiredLumens && lightProducts.length > 0) {
        const randomLight = lightProducts[Math.floor(Math.random() * lightProducts.length)];
        const [x, y, width, height] = room.bbox;
        lights.push({
          id: `${room.id}-${lights.length}`,
          x: x + Math.random() * width,
          y: y + Math.random() * height,
          productId: randomLight.id,
        });
        currentLumens += randomLight.lumens;
      }

      return { ...room, lights };
    });

    setRooms(updatedRooms);
  };

  useEffect(() => {
    if (image && canvasRef.current && imageRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        canvas.width = imageRef.current.width;
        canvas.height = imageRef.current.height;
        ctx.drawImage(imageRef.current, 0, 0);

        rooms.forEach(room => {
          const [x, y, width, height] = room.bbox;
          ctx.strokeStyle = 'blue';
          ctx.lineWidth = 2;
          ctx.strokeRect(x, y, width, height);
          ctx.fillStyle = 'blue';
          ctx.font = '16px Arial';
          ctx.fillText(room.name, x, y - 5);

          room.lights.forEach(light => {
            ctx.beginPath();
            ctx.arc(light.x, light.y, 5, 0, 2 * Math.PI);
            ctx.fillStyle = 'yellow';
            ctx.fill();
          });
        });
      }
    }
  }, [image, rooms]);

  return (
    <ChakraProvider>
      <Box minH="100vh" py={10}>
        <Container maxW="container.xl">
          <VStack spacing={8}>
            <Heading>Apartment Lighting Calculator</Heading>
            {modelLoading && (
              <Box width="100%">
                <Text mb={2}>Loading object detection model...</Text>
                <Progress size="sm" isIndeterminate />
              </Box>
            )}
            <Button as="label" htmlFor="upload-image" isDisabled={!modelLoaded}>
              Upload Layout
              <input
                id="upload-image"
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
              />
            </Button>
            {imageUploaded && (
              <Box position="relative" width="100%">
                <Image
                  ref={imageRef}
                  src={image || ''}
                  alt="Apartment Layout"
                  width="100%"
                  height="auto"
                  onLoad={() => {
                    if (canvasRef.current && imageRef.current) {
                      canvasRef.current.width = imageRef.current.width;
                      canvasRef.current.height = imageRef.current.height;
                    }
                  }}
                />
                <canvas 
                  ref={canvasRef}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                  }}
                  onMouseMove={(e) => {
                    const rect = canvasRef.current?.getBoundingClientRect();
                    if (rect) {
                      const x = (e.clientX - rect.left) * (canvasRef.current?.width || 1) / rect.width;
                      const y = (e.clientY - rect.top) * (canvasRef.current?.height || 1) / rect.height;
                      const light = rooms.flatMap(r => r.lights).find(l => 
                        Math.abs(l.x - x) < 5 && Math.abs(l.y - y) < 5
                      );
                      setHoveredLight(light || null);
                    }
                  }}
                />
              </Box>
            )}
            {loading ? (
              <Spinner />
            ) : (
              <>
                <Button onClick={analyzeLayout} isDisabled={!imageUploaded || !modelLoaded}>Analyze Layout</Button>
                <Button onClick={calculateLighting} isDisabled={rooms.length === 0} colorScheme="blue">
                  Calculate Lighting
                </Button>
              </>
            )}
            {rooms.map(room => (
              <Box key={room.id} borderWidth={1} p={4} borderRadius="md" width="100%">
                <Text fontWeight="bold">{room.name}</Text>
                <Text>Area: {room.area.toFixed(2)} m²</Text>
                <Text>Lights: {room.lights.length}</Text>
                <Text>Total Lumens: {room.lights.reduce((sum, light) => sum + (lightProducts.find(p => p.id === light.productId)?.lumens || 0), 0)}</Text>
              </Box>
            ))}
          </VStack>
        </Container>
      </Box>
    </ChakraProvider>
  );
}

export default App;
